// src/components/Dashboard.tsx
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import femaleProfilePic from "../../assets/profile_pic_female.png";
import closeIcon from "../../assets/close_icon.png";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios"; // Import axios for API calls
import "./dashboard.scss"; // Create and import a CSS file for styling

// Define TypeScript interface for user details
interface UserDetails {
  name: string;
  email: string;
  phoneNumber: string;
  role: string;
  gender: string;
}

const Dashboard: React.FC = () => {
  const [role, setRole] = useState<string>(""); // State to store role
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null); // State to store user details
  const [showOverlay, setShowOverlay] = useState<boolean>(false); // State to toggle overlay
  const [loading, setLoading] = useState<boolean>(true); // Add loading state
  const navigate = useNavigate();
  const isInitialRender = useRef(true); // Track initial render

  const [totalActiveChatSupportCount, setTotalActiveChatSupportCount] =
    useState(0);
  const [totalCallbackRequests, setTotalCallbackRequests] = useState(0);
  const [totalDietsToBeSent, setTotalDietsToBeSent] = useState(0);

  const [conversationCount, setConversationCount] = useState(0);

  // Function to fetch user details from the API
  const fetchUserDetails = async () => {
    try {
      // Get email and token from localStorage
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");

      // If email or token is missing, redirect to login
      if (!email || !token) {
        navigate("/login");
        return;
      }

      // Configure headers for the API request
      const config = {
        headers: {
          Authorization: `Bearer ${token}`, // Set JWT token in the Authorization header
        },
      };

      // Make API call to fetch user details
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );

      // Set user details and role in state
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchConversations();
      fetchAllUsersDietPlan();
      fetchAllChatSupports(token);
      fetchAllCallBackrequests(token);
    } catch (error) {
      handleLogout(); // Logout and redirect if there's an error
    } finally {
      setLoading(false); // Stop the loading indicator once API responds
    }
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem("token"); // Clear token on logout
    localStorage.removeItem("email"); // Clear email on logout
    navigate("/login"); // Redirect to login
  };

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/chat/admin/conversations`
      );
      const fetchedConversations = response.data; // Store the array locally

      // 2. Count the number of items and store it in state
      setConversationCount(fetchedConversations.length); // 💡 THIS IS THE COUNT!
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchConversations();
  //   // Optional: Polling to refresh the list every X seconds
  //   const intervalId = setInterval(fetchConversations, 10000); // Refresh every 10 seconds
  //   return () => clearInterval(intervalId);
  // }, []);

  // useEffect hook to fetch user details on component mount
  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false; // Prevent further calls
    }
    const intervalId = setInterval(fetchUserDetails, 10000); // Refresh every 10 seconds
    return () => clearInterval(intervalId);
  }, []);

  // Fetch All Chat Supports Requests
  const fetchAllChatSupports = async (token: string) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/fetch-all-users-query-fitbot`,
        config
      );

      // Extracting data
      const allRequests = response.data.data;

      // Counting total active tickets where isTicketOpen is true
      const activeChatSupportCount = allRequests.reduce((count, request) => {
        return (
          count + request.tickets.filter((ticket) => ticket.isTicketOpen).length
        );
      }, 0);

      setTotalActiveChatSupportCount(activeChatSupportCount);

      console.log(
        "Total Active Notification Count:",
        totalActiveChatSupportCount
      );
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  // All Callback Requests
  const fetchAllCallBackrequests = async (token: string) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-callback-requests`,
        config
      );

      // Extracting data
      const allRequests = response.data.data;

      // Counting total callback requests where isResolved is false
      const callbackRequests = allRequests.filter(
        (request) => !request.isResolved
      ).length;

      setTotalCallbackRequests(callbackRequests);

      console.log(
        "Total Callback Requests (Pending Resolution):",
        totalCallbackRequests
      );
    } catch (error) {
      console.error("Failed to fetch callback requests:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all diet plans to be sent

  const fetchAllUsersDietPlan = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-users-info`
      );
      const allUsers = response.data.data;

      // Count users who have not received all diets (totalDietsSent < totalDiets) and have not yet had their diet plan sent (dietPlanSent: false)
      const totalNumberOfDietsToBeSent = allUsers.filter(
        (user: any) =>
          user.totalDietsSent < user.totalDiets && user.dietPlanSent === false
      ).length;

      setTotalDietsToBeSent(totalNumberOfDietsToBeSent);

      console.log("Total Diets To Be Sent:", totalDietsToBeSent);
    } catch (error) {
      console.error("Failed to fetch users with their diet plans:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Header Section */}
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              {/* Display Male or Female profile based on user's gender */}
              {/* <img
            className="profile-pic"
            src={
              userDetails?.gender == "Male"
                ? maleProfilePic
                : femaleProfilePic
            }
            alt="Profile"
          /> */}
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {/* Overlay for showing user details */}
          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* Dashboard Cards Section */}
          <div className="cards-container">
            <div className="cards-grid">
              {(role == "Owner" || role == "Developer") && (
                <div
                  className="card"
                  onClick={() => navigate("/employee-portal")}
                >
                  <h3>Employee Panel</h3>
                  <p>
                    Add new employee (Admin or Content writers), Remove an
                    employee, View employee's detail.
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/generate-payment-link")}
                >
                  <h3>Payment Link</h3>
                  <p>Generate Payment Link and send it to clients to make the payments</p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/live-website-chat")}
                >
                  {conversationCount > 0 ? (
                    <span className="notification-badge">
                      {conversationCount}
                    </span>
                  ) : (
                    ""
                  )}
                  <h3>Live Chat (Website)</h3>
                  <p>
                    Connect with customers who contacted us from our website
                  </p>
                </div>
              )}
              {/* {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/interested-clients")}
                >
                  <h3>All Interested Clients</h3>
                  <p>List of those clients who are interested</p>
                </div>
              )} */}
              {/* {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div className="card" onClick={() => navigate("/client-panel")}>
                  <h3>Client Panel</h3>
                  <p>
                    Manage Client's Details, manage Diet plans, Progress
                    Reports/Trackers details of clients, Orders.
                  </p>
                </div>
              )} */}
              {(role == "Owner" ||
                role == "Developer" ||
                role == "Admin" ||
                role == "Dietitian") && (
                <div className="card" onClick={() => navigate("/diet-panel")}>
                  <h3>Create Diets</h3>
                  <p>Create new Diet/ Add Diet Category/ Name Diet Plan.</p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin" || role == "Dietitian") && (
                <div className="card" onClick={() => navigate("/send-diets")}>
                  {totalDietsToBeSent > 0 ? (
                    <span className="notification-badge">
                      {totalDietsToBeSent}
                    </span>
                  ) : (
                    ""
                  )}
                  <h3>Send Diets</h3>
                  <p>Manage Client's Diet (Send or Edit diet of any client)</p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/report-tracker")}
                >
                  <h3>Report Tracker</h3>
                  <h4 style={{ color: "white", marginTop: "-15px" }}>
                    (Registered Users)
                  </h4>
                  <p>
                    Reports/Trackers details of clients, search users using
                    their mobile numbers.
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/report-tracker-not-registered")}
                >
                  <h3>Report Tracker</h3>
                  <h4 style={{ color: "white", marginTop: "-15px" }}>
                    (Non-Registered Users)
                  </h4>
                  <p>
                    Reports/Trackers details of clients, search users using
                    their mobile numbers.
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div className="card" onClick={() => navigate("/sessions")}>
                  <h3>Sessions</h3>
                  <p>Diet sessions, Yoga Sessions, Fitness Sessions</p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/subscription-alert")}
                >
                  <h3>Subscription Alert</h3>
                  <p>Send Subscription Alert to subscribed clients.</p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/callback-requests")}
                >
                  {totalCallbackRequests > 0 ? (
                    <span className="notification-badge">
                      {totalCallbackRequests}
                    </span>
                  ) : (
                    ""
                  )}
                  <h3>Callback Requests</h3>
                  <p>
                    Details of clients who book a call back, their mobile no and
                    other details.
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/banner-manager")}
                >
                  <h3>Banner Manager</h3>
                  <p>Manage Banners, Create and upload new Banners.</p>
                </div>
              )}
              {(role == "Owner" ||
                role == "Developer" ||
                role == "Admin" ||
                role == "Content Writer") && (
                <div className="card" onClick={() => navigate("/blogs")}>
                  <h3>Blogs</h3>
                  <p>
                    Create blogs, Mark them as a Premium or no non premium,
                    Upload blogs.
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/transformation")}
                >
                  <h3>Transformation</h3>
                  <p>
                    Upload 'before' and 'after' pictures along with details of
                    clients who have transformed their bodies.
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/e-commerce-portal")}
                >
                  <h3>E-Commerce Portal</h3>
                  <p>
                    Upload product, Add description, Add Categories, Add Price,
                    Add Weight, Manage all other shipping details.
                  </p>
                </div>
              )}
              {/* <div className="card" onClick={() => navigate("/payments")}>
                <h3>Payments</h3>
                <p>Track client payments, invoices, and transaction history.</p>
              </div> */}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div className="card" onClick={() => navigate("/chat-support")}>
                  {totalActiveChatSupportCount > 0 ? (
                    <span className="notification-badge">
                      {totalActiveChatSupportCount}
                    </span>
                  ) : (
                    ""
                  )}
                  <h3>Chat support</h3>
                  <p>
                    Interact with clients who contacted us and resolve their
                    issues.
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/send-notification")}
                >
                  <h3>Send Notification</h3>
                  <p>Manage notification, Send notification.</p>
                </div>
              )}
              {/* {(role == "Owner" || role == "Developer") && (
                <div className="card" onClick={() => navigate("/coupon-codes")}>
                  <h3>Coupon Codes</h3>
                  <p>Create or Manage the Coupon Codes</p>
                </div>
              )} */}
              {(role == "Owner" || role == "Developer") && (
                <div
                  className="card"
                  onClick={() => navigate("/referral-codes")}
                >
                  <h3>Referral Codes</h3>
                  <p>Create or Manage the Referral Codes</p>
                </div>
              )}
              {(role == "Owner" || role == "Developer") && (
                <div
                  className="card"
                  onClick={() => navigate("/subscription-plans")}
                >
                  <h3>Subscription Plans</h3>
                  <p>Create and Manage Subscription plans</p>
                </div>
              )}
              {/* {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/festival-sales-products")}
                >
                  <h3>Create Festival Sales Offer for Products</h3>
                  <p>
                  Create Festival Sales Offers on any Product
                  </p>
                </div>
              )} */}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/festival-sales-subscription")}
                >
                  <h3>Create Festival Sales</h3>
                  <p>Create Festival Sales Offers on any Subscription Plans</p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/user-statistics")}
                >
                  <h3>User Statistics</h3>
                  <p>
                    Do you know how many total users have registered in Fitoja
                    App?
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/lead-form-meta")}
                >
                  <h3>Meta Lead Form</h3>
                  <p>
                    List of all clients who filled the form (via Meta) and/or
                    sent an SMS on whatsapp.
                  </p>
                </div>
              )}
              {(role == "Owner" || role == "Developer" || role == "Admin") && (
                <div
                  className="card"
                  onClick={() => navigate("/lead-form-google")}
                >
                  <h3>Google Lead Form</h3>
                  <p>
                    Do you know how many total users have registered in Fitoja
                    App?
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
